
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";

const StudentPage = () => {
  const navigate = useNavigate();
  
  const handleNavigateBack = () => {
    navigate(-1);
    setTimeout(() => {
      const section = document.getElementById('ns2');
      if (section) {
        const offset = -80;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset + offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }, 100);
  };

  const handleNextStepsClick = () => {
    navigate('/');
    setTimeout(() => {
      const section = document.getElementById('next-steps');
      if (section) {
        const offset = -80;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset + offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }, 100);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: images } = useQuery({
    queryKey: ["images", "student"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("images")
        .select("*")
        .eq("section", "student")
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const getBackgroundImage = () => {
    if (images && images.length > 0) {
      const { data } = supabase.storage
        .from('images')
        .getPublicUrl(images[0].storage_path);
      return data.publicUrl;
    }
    return 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&q=80';
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#243949] to-[#517fa4]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 p-2 h-auto group"
            onClick={handleNavigateBack}
          >
            <div className="flex items-center animate-horizontal-bounce">
              <ChevronLeft className="w-4 h-4" />
              <ChevronLeft className="w-4 h-4 -ml-2" />
            </div>
          </Button>
          
          <h2 className="text-lg md:text-xl font-bold text-white text-center uppercase flex-grow">
            DAS FALSCHE STUDIUM
          </h2>
          
          <div className="w-[40px]"></div>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          <div className="aspect-[21/9] w-full mb-8">
            <img
              src={getBackgroundImage()}
              alt="Student Beratung"
              className="rounded-lg object-cover w-full h-full border border-[#9F9EA1]"
            />
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border-2 hover:border-white/40 transition-all duration-300">
            <h3 className="text-base font-semibold text-white mb-4 uppercase">DIE HERAUSFORDERUNG</h3>
            <p className="text-base text-white text-left">
              Du hast gedacht, dein Studium wäre der richtige Weg – doch jetzt zweifelst du. Die Vorlesungen interessieren dich nicht wirklich und du fragst dich immer öfter: War das die richtige Entscheidung? Einfach abbrechen oder wechseln? Klingt logisch – aber was, wenn du dann wieder falsch liegst? Der Druck ist groß: Wenn du jetzt eine neue Richtung einschlägst, dann muss es diesmal die richtige sein.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border-2 hover:border-white/40 transition-all duration-300">
            <h3 className="text-base font-semibold text-white mb-4 uppercase">WIE ICH DIR HELFEN KANN</h3>
            <ul className="space-y-2 text-base text-white text-left">
              <li className="flex items-start">
                <ChevronRight className="w-4 h-4 flex-shrink-0 mr-2 mt-1" />
                <p className="text-base text-white">
                  Analyse deiner aktuellen Situation und der Gründe für deine Unzufriedenheit
                </p>
              </li>
              <li className="flex items-start">
                <ChevronRight className="w-4 h-4 flex-shrink-0 mr-2 mt-1" />
                <p className="text-base text-white">
                  Evaluation deiner wahren Leidenschaften, deiner Stärken & auch Schwächen
                </p>
              </li>
              <li className="flex items-start">
                <ChevronRight className="w-4 h-4 flex-shrink-0 mr-2 mt-1" />
                <p className="text-base text-white">
                  Strukturierte Erarbeitung von alternativen Studiengängen/Berufswegen
                </p>
              </li>
              <li className="flex items-start">
                <ChevronRight className="w-4 h-4 flex-shrink-0 mr-2 mt-1" />
                <p className="text-base text-white">
                  Prüfung von Anrechnungsmöglichkeiten bereits erbrachter Studienleistungen
                </p>
              </li>
              <li className="flex items-start">
                <ChevronRight className="w-4 h-4 flex-shrink-0 mr-2 mt-1" />
                <p className="text-base text-white">
                  Entwicklung eines konkreten Handlungsplans für deine berufliche Neuorientierung
                </p>
              </li>
            </ul>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border-2 hover:border-white/40 transition-all duration-300">
            <h3 className="text-base font-semibold text-white mb-4 uppercase">DEIN NÄCHSTER SCHRITT</h3>
            <p className="text-base text-white text-left mb-6">
              In einem persönlichen Gespräch können wir gemeinsam herausfinden, wie ich dich bei deiner Neuorientierung am besten unterstützen kann.
            </p>
            <Button 
              onClick={handleNextStepsClick}
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-[#ECA505] px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all cursor-pointer mx-auto"
            >
              Interesse am Coaching mit mir? <div className="flex items-center">
                <ChevronRight className="w-4 h-4" />
                <ChevronRight className="w-4 h-4 -ml-2" />
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentPage;
